import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useNavigate } from 'react-router-dom';
import { API } from '@/App';
import { 
  FileText, Plus, Trash2, Check, IndianRupee, Calendar, 
  Package, Search, Building2, ChevronDown, ChevronRight, Send
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';

/**
 * VENDOR QUOTATION CREATION
 * 
 * Key Rules:
 * - Vendor can ONLY add items from their APPROVED rate card
 * - Cannot enter free-text items
 * - Quantity is editable, description/rate is locked
 * - Requires selecting a project they are assigned to
 */

export default function VendorQuotationCreate() {
  const [projects, setProjects] = useState([]);
  const [approvedRates, setApprovedRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const navigate = useNavigate();
  
  // Form state
  const [selectedProject, setSelectedProject] = useState('');
  const [selectedItems, setSelectedItems] = useState([]); // { rate_id, quantity, rate_obj }
  const [notes, setNotes] = useState('');
  const [validUntil, setValidUntil] = useState(
    new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  
  // Rate selector sheet
  const [showRateSelector, setShowRateSelector] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [projectsRes, ratesRes] = await Promise.all([
        axios.get(`${API}/projects`),
        axios.get(`${API}/vendor/my-rates`)
      ]);
      
      setProjects(projectsRes.data);
      // Filter only approved rates - handle both array and object response
      const ratesData = ratesRes.data.rates || ratesRes.data;
      const approved = (Array.isArray(ratesData) ? ratesData : []).filter(r => 
        ['approved', 'locked', 'active'].includes(r.status)
      );
      setApprovedRates(approved);
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  // Group rates by category
  const ratesByCategory = approvedRates.reduce((acc, rate) => {
    const category = rate.category_name || 'Uncategorized';
    if (!acc[category]) acc[category] = [];
    acc[category].push(rate);
    return acc;
  }, {});

  // Filter rates by search
  const filterRates = (rates) => {
    if (!searchQuery) return rates;
    const query = searchQuery.toLowerCase();
    return rates.filter(r => 
      r.item_name?.toLowerCase().includes(query) ||
      r.description?.toLowerCase().includes(query)
    );
  };

  const toggleCategory = (category) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(category)) next.delete(category);
      else next.add(category);
      return next;
    });
  };

  const addItem = (rate) => {
    // Check if already added
    if (selectedItems.some(item => item.rate_id === rate.id)) {
      toast.info('Item already added');
      return;
    }
    
    setSelectedItems(prev => [...prev, {
      rate_id: rate.id,
      quantity: 1,
      rate_obj: rate
    }]);
    toast.success(`Added: ${rate.item_name}`);
  };

  const removeItem = (rateId) => {
    setSelectedItems(prev => prev.filter(item => item.rate_id !== rateId));
  };

  const updateQuantity = (rateId, quantity) => {
    if (quantity < 0.01) return;
    setSelectedItems(prev => prev.map(item => 
      item.rate_id === rateId ? { ...item, quantity } : item
    ));
  };

  // Calculate totals
  const subtotal = selectedItems.reduce((sum, item) => 
    sum + (item.quantity * (item.rate_obj?.rate || 0)), 0
  );
  const gst = subtotal * 0.18;
  const total = subtotal + gst;

  const handleSubmit = async () => {
    if (!selectedProject) {
      toast.error('Please select a project');
      return;
    }
    if (selectedItems.length === 0) {
      toast.error('Please add at least one item from your rate card');
      return;
    }
    
    setSubmitting(true);
    try {
      await axios.post(`${API}/vendor/quotations`, {
        project_id: selectedProject,
        items: selectedItems.map(item => ({
          vendor_rate_id: item.rate_id,
          quantity: item.quantity,
          notes: ''
        })),
        valid_until: new Date(validUntil).toISOString(),
        notes: notes
      });
      
      toast.success('Quotation submitted successfully');
      navigate('/quotations');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit quotation');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="text-stone-400">Loading...</div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6" data-testid="vendor-quotation-create">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-manrope text-3xl md:text-4xl font-bold tracking-tight text-stone-900 mb-2">
              Create Quotation
            </h1>
            <p className="text-stone-500">
              Select items from your approved rate card to create a quotation
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/quotations')}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit} 
              disabled={submitting || selectedItems.length === 0}
              className="apple-button"
              data-testid="submit-quotation-btn"
            >
              <Send size={16} className="mr-2" />
              {submitting ? 'Submitting...' : 'Submit Quotation'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Project & Items */}
          <div className="lg:col-span-2 space-y-6">
            {/* Project Selection */}
            <div className="apple-card p-6">
              <Label className="text-base font-semibold mb-3 block">Project *</Label>
              <Select value={selectedProject} onValueChange={setSelectedProject}>
                <SelectTrigger className="w-full" data-testid="project-select">
                  <SelectValue placeholder="Select the project for this quotation" />
                </SelectTrigger>
                <SelectContent>
                  {projects.map(p => (
                    <SelectItem key={p.id} value={p.id}>
                      <div className="flex items-center gap-2">
                        <Building2 size={14} />
                        {p.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Selected Items */}
            <div className="apple-card p-6">
              <div className="flex items-center justify-between mb-4">
                <Label className="text-base font-semibold">Quotation Items</Label>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowRateSelector(true)}
                  data-testid="add-item-btn"
                >
                  <Plus size={16} className="mr-1" /> Add from Rate Card
                </Button>
              </div>
              
              {selectedItems.length === 0 ? (
                <div className="text-center py-12 border-2 border-dashed border-stone-200 rounded-lg">
                  <Package size={40} className="mx-auto text-stone-300 mb-3" />
                  <p className="text-stone-500">No items added yet</p>
                  <Button 
                    variant="link" 
                    className="mt-2 text-red-600"
                    onClick={() => setShowRateSelector(true)}
                  >
                    Add items from your approved rate card
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {selectedItems.map((item, idx) => (
                    <div 
                      key={item.rate_id} 
                      className="border rounded-lg p-4 bg-stone-50"
                      data-testid={`quotation-item-${idx}`}
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1 min-w-0">
                          <h4 className="font-medium text-stone-900">
                            {item.rate_obj?.item_name}
                          </h4>
                          <p className="text-sm text-stone-500 line-clamp-2">
                            {item.rate_obj?.description || 'No description'}
                          </p>
                          <div className="flex items-center gap-4 mt-2 text-sm text-stone-600">
                            <span>₹{item.rate_obj?.rate?.toLocaleString()} / {item.rate_obj?.unit}</span>
                            {item.rate_obj?.category_name && (
                              <Badge variant="outline" className="text-xs">
                                {item.rate_obj.category_name}
                              </Badge>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className="flex items-center gap-1">
                            <Label className="text-xs text-stone-500 sr-only">Qty</Label>
                            <Input
                              type="number"
                              min="0.01"
                              step="0.01"
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.rate_id, parseFloat(e.target.value) || 0)}
                              className="w-20 text-center"
                              data-testid={`item-quantity-${idx}`}
                            />
                            <span className="text-xs text-stone-500 w-12">{item.rate_obj?.unit}</span>
                          </div>
                          
                          <div className="text-right min-w-24">
                            <p className="font-semibold text-stone-900">
                              ₹{(item.quantity * (item.rate_obj?.rate || 0)).toLocaleString()}
                            </p>
                          </div>
                          
                          <Button 
                            variant="ghost" 
                            size="sm"
                            className="text-red-500 hover:text-red-700 hover:bg-red-50"
                            onClick={() => removeItem(item.rate_id)}
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Notes */}
            <div className="apple-card p-6">
              <Label className="text-base font-semibold mb-3 block">Additional Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional terms, conditions, or notes..."
                rows={3}
              />
            </div>
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-1">
            <div className="apple-card p-6 sticky top-6">
              <h3 className="font-manrope text-lg font-bold mb-4">Quotation Summary</h3>
              
              <div className="space-y-3 mb-6">
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Items</span>
                  <span className="font-medium">{selectedItems.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">Subtotal</span>
                  <span className="font-medium">₹{subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-stone-500">GST (18%)</span>
                  <span className="font-medium">₹{gst.toLocaleString()}</span>
                </div>
                <div className="border-t pt-3">
                  <div className="flex justify-between">
                    <span className="font-semibold">Total</span>
                    <span className="font-bold text-xl text-green-700">
                      ₹{total.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <div className="space-y-3">
                <div>
                  <Label className="text-sm mb-1 block">Valid Until *</Label>
                  <Input
                    type="date"
                    value={validUntil}
                    onChange={(e) => setValidUntil(e.target.value)}
                    min={new Date().toISOString().split('T')[0]}
                    data-testid="valid-until-input"
                  />
                </div>
              </div>
              
              <div className="mt-6 p-3 bg-amber-50 rounded-lg">
                <p className="text-xs text-amber-800">
                  <strong>Note:</strong> Quotation items are based on your approved rate card. 
                  Rates and descriptions cannot be modified. Only quantity is editable.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Rate Card Selector Sheet */}
        <Sheet open={showRateSelector} onOpenChange={setShowRateSelector}>
          <SheetContent className="sm:max-w-xl overflow-y-auto">
            <SheetHeader>
              <SheetTitle className="font-manrope text-xl">
                Select from Rate Card
              </SheetTitle>
            </SheetHeader>
            
            <div className="mt-4 space-y-4">
              {/* Search */}
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search approved rates..."
                  className="pl-9"
                />
              </div>
              
              {approvedRates.length === 0 ? (
                <div className="text-center py-12">
                  <Package size={40} className="mx-auto text-stone-300 mb-3" />
                  <p className="text-stone-500">No approved rates available</p>
                  <p className="text-sm text-stone-400 mt-1">
                    Submit rates for approval first
                  </p>
                </div>
              ) : (
                <div className="space-y-2">
                  {Object.entries(ratesByCategory).map(([category, rates]) => {
                    const filteredRates = filterRates(rates);
                    if (filteredRates.length === 0) return null;
                    
                    const isExpanded = expandedCategories.has(category);
                    
                    return (
                      <div key={category} className="border rounded-lg overflow-hidden">
                        <button
                          onClick={() => toggleCategory(category)}
                          className="w-full flex items-center justify-between p-3 bg-stone-50 hover:bg-stone-100 transition-colors"
                        >
                          <span className="font-medium text-stone-700">{category}</span>
                          <div className="flex items-center gap-2">
                            <Badge variant="outline">{filteredRates.length}</Badge>
                            {isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                          </div>
                        </button>
                        
                        {isExpanded && (
                          <div className="divide-y">
                            {filteredRates.map(rate => {
                              const isAdded = selectedItems.some(item => item.rate_id === rate.id);
                              
                              return (
                                <div 
                                  key={rate.id}
                                  className="p-3 flex items-center justify-between hover:bg-stone-50"
                                >
                                  <div className="flex-1 min-w-0 mr-3">
                                    <p className="font-medium text-stone-900 truncate">
                                      {rate.item_name}
                                    </p>
                                    <p className="text-sm text-stone-500">
                                      ₹{rate.rate?.toLocaleString()} / {rate.unit}
                                    </p>
                                  </div>
                                  
                                  <Button
                                    size="sm"
                                    variant={isAdded ? "outline" : "default"}
                                    onClick={() => addItem(rate)}
                                    disabled={isAdded}
                                    className={isAdded ? "text-green-600" : ""}
                                  >
                                    {isAdded ? (
                                      <><Check size={14} className="mr-1" /> Added</>
                                    ) : (
                                      <><Plus size={14} className="mr-1" /> Add</>
                                    )}
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </Layout>
  );
}
